import { nanoid } from 'nanoid';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import { isOrgInFreeTier } from '../../../../helpers/subscriptions';
import { dropAt } from '../../../../utils';
import Button from '../../../generic/Button/Button';
import NavigationButton from '../../../generic/Button/NavigationButton';
import PaidFeatureGate from '../../../generic/Gates/PaidFeatureGate';
import { icons, IconText } from '../../../generic/Icon';
import { DOCS_URL } from '../../../generic/Link';
import Loader from '../../../generic/Loader';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import useLanguageSettings from '../useLanguageSettings';
import type { PipRepository } from '../../../../../typings/auth';

import Repository from './Repository';

const PYTHON_DOCS_LINK = `${DOCS_URL}/python`;

const PipSettings: VoidFunctionComponent = () => {
  const {
    settings,
    isSaving,
    onUpdateSettings,
    onSaveSettings,
    isLoading,
    isSaveButtonDisabled,
  } = useLanguageSettings(
    window.user?.organizationId as number,
    'pip',
  );

  const repositories = settings?.repositories || [];

  const onAddRepository = () =>
    onUpdateSettings({
      repositories: [
        ...repositories,
        { _id: nanoid() },
      ],
    });

  const onRemoveRepository = (index: number) =>
    onUpdateSettings({
      repositories: dropAt(repositories, index),
    });

  const onUpdateRepository = <K extends keyof PipRepository>(
    index: number,
    member: K,
    value: PipRepository[K],
  ) =>
      onUpdateSettings({
        repositories: repositories.map((repo, i): PipRepository => {
          if (i !== index) {
            return repo;
          }

          return {
            ...repo,
            [member]: value,
            hasPassword: member === 'password' ? false : repo.hasPassword,
          };
        }),
      });

  const isFreeOrg = isOrgInFreeTier();

  return (
    <PaidFeatureGate isRestricted={isFreeOrg} hidePill>
      <Header heading="Pip Settings">
        <Header.SaveButton
          onClick={onSaveSettings}
          isDisabled={isSaveButtonDisabled}
          isLoading={isSaving}
        />
      </Header>
      {isLoading
        ? <Loader />
        : (
          <Section>
            <Section.Left heading="Pip Repositories">
              <Section.Paragraph>
                If you use a private or non-standard PyPI repository,
                {' '}
                add it here so that FOSSA can resolve and analyze those dependencies.
              </Section.Paragraph>
              <NavigationButton
                to={PYTHON_DOCS_LINK}
                size="medium"
                type="secondary"
                openInNewTab
              >
                View documentation
              </NavigationButton>
            </Section.Left>
            <Section.Right>
              {repositories.map((repo, index) =>
                <Repository
                  repository={repo}
                  onRemoveRepository={() => onRemoveRepository(index)}
                  onUpdateRepository={
                    <K extends keyof PipRepository>(
                      member: K,
                      value: PipRepository[K],
                    ) => {
                      onUpdateRepository(index, member, value);
                    }
                  }
                />,
              )}
              <Button
                type="secondary"
                size="medium"
                onClick={onAddRepository}
              >
                <IconText icon={icons.plus} text="Add repository" />
              </Button>
            </Section.Right>
          </Section>
          )}
    </PaidFeatureGate>
  );
};

export default PipSettings;
